import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'

export const ACTIVITY_PAGE_SIZE = 50

export type ActivityEventType =
    | 'project_created'
    | 'project_deleted'
    | 'task_created'
    | 'task_deleted'
    | 'task_status_changed'
    | 'task_assigned'
    | 'task_renamed'
    | 'task_description_changed'
    | 'task_labels_changed'
    | 'task_moved'
    | 'comment_added'

export interface ActivityEventMeta {
    projectName?: string
    taskName?: string
    taskNumber?: number
    fromStatusId?: string
    toStatusId?: string
    fromAssigneeId?: string | null
    toAssigneeId?: string | null
    fromName?: string
    fromListId?: string
    toListId?: string
    labelIds?: string[]
    commentId?: string
}

export interface ActivityEvent {
    id: string
    type: ActivityEventType
    workspaceId: string
    projectId: string
    taskId?: string
    actorId: string | null
    actorName?: string | null
    source?: 'api' | 'mcp' | null
    createdAt: any
    meta: ActivityEventMeta
}

interface ProjectActivityState {
    events: ActivityEvent[]
    hasMore: boolean
    limit: number
}

export const WORKSPACE_ACTIVITY_INITIAL_LIMIT = 100

interface ActivityState {
    byTaskId: Record<string, ActivityEvent[]>
    loadingTaskIds: string[]
    byProjectId: Record<string, ProjectActivityState>
    loadingProjectIds: string[]
    workspaceEvents: ActivityEvent[]
    workspaceLoading: boolean
    workspaceHasMore: boolean
    workspaceLimit: number
}

const initialState: ActivityState = {
    byTaskId: {},
    loadingTaskIds: [],
    byProjectId: {},
    loadingProjectIds: [],
    workspaceEvents: [],
    workspaceLoading: false,
    workspaceHasMore: false,
    workspaceLimit: WORKSPACE_ACTIVITY_INITIAL_LIMIT,
}

const activitySlice = createSlice({
    name: 'activity',
    initialState,
    reducers: {
        // Saga triggers
        subscribeTaskActivitySaga: (_state, _action: PayloadAction<string>) => {},
        unsubscribeTaskActivitySaga: (_state, _action: PayloadAction<string>) => {},
        subscribeProjectActivitySaga: (_state, _action: PayloadAction<string>) => {},
        unsubscribeProjectActivitySaga: (_state, _action: PayloadAction<string>) => {},
        loadMoreProjectActivitySaga: (_state, _action: PayloadAction<string>) => {},
        subscribeWorkspaceActivitySaga: (_state, _action: PayloadAction<string>) => {},
        unsubscribeWorkspaceActivitySaga: (_state) => {},
        loadMoreWorkspaceActivitySaga: (_state) => {},

        // State mutations
        setTaskActivity: (state, action: PayloadAction<{ taskId: string; events: ActivityEvent[] }>) => {
            const { taskId, events } = action.payload
            state.byTaskId[taskId] = events
        },
        setTaskActivityLoading: (state, action: PayloadAction<{ taskId: string; loading: boolean }>) => {
            const { taskId, loading } = action.payload
            if (loading) {
                if (!state.loadingTaskIds.includes(taskId)) {
                    state.loadingTaskIds.push(taskId)
                }
            } else {
                state.loadingTaskIds = state.loadingTaskIds.filter(id => id !== taskId)
            }
        },
        clearTaskActivity: (state, action: PayloadAction<string>) => {
            delete state.byTaskId[action.payload]
        },
        setProjectActivity: (state, action: PayloadAction<{ projectId: string; events: ActivityEvent[]; hasMore: boolean }>) => {
            const { projectId, events, hasMore } = action.payload
            const existing = state.byProjectId[projectId]
            state.byProjectId[projectId] = { events, hasMore, limit: existing?.limit ?? ACTIVITY_PAGE_SIZE }
        },
        incrementProjectActivityLimit: (state, action: PayloadAction<string>) => {
            const projectId = action.payload
            const existing = state.byProjectId[projectId]
            const currentLimit = existing?.limit ?? ACTIVITY_PAGE_SIZE
            state.byProjectId[projectId] = {
                events: existing?.events ?? [],
                hasMore: existing?.hasMore ?? false,
                limit: currentLimit + ACTIVITY_PAGE_SIZE,
            }
        },
        setProjectActivityLoading: (state, action: PayloadAction<{ projectId: string; loading: boolean }>) => {
            const { projectId, loading } = action.payload
            if (loading) {
                if (!state.loadingProjectIds.includes(projectId)) {
                    state.loadingProjectIds.push(projectId)
                }
            } else {
                state.loadingProjectIds = state.loadingProjectIds.filter(id => id !== projectId)
            }
        },
        clearProjectActivity: (state, action: PayloadAction<string>) => {
            delete state.byProjectId[action.payload]
        },
        setWorkspaceActivity: (state, action: PayloadAction<{ events: ActivityEvent[]; hasMore: boolean }>) => {
            state.workspaceEvents = action.payload.events
            state.workspaceHasMore = action.payload.hasMore
        },
        incrementWorkspaceActivityLimit: (state) => {
            state.workspaceLimit = state.workspaceLimit + ACTIVITY_PAGE_SIZE
        },
        setWorkspaceActivityLoading: (state, action: PayloadAction<boolean>) => {
            state.workspaceLoading = action.payload
        },
    },
})

export const {
    subscribeTaskActivitySaga,
    unsubscribeTaskActivitySaga,
    subscribeProjectActivitySaga,
    unsubscribeProjectActivitySaga,
    loadMoreProjectActivitySaga,
    subscribeWorkspaceActivitySaga,
    unsubscribeWorkspaceActivitySaga,
    setTaskActivity,
    setTaskActivityLoading,
    clearTaskActivity,
    setProjectActivity,
    incrementProjectActivityLimit,
    setProjectActivityLoading,
    clearProjectActivity,
    loadMoreWorkspaceActivitySaga,
    setWorkspaceActivity,
    incrementWorkspaceActivityLimit,
    setWorkspaceActivityLoading,
} = activitySlice.actions

export default activitySlice.reducer
